import { useState, useCallback } from 'react';

export interface LocationCoords {
  latitude: number;
  longitude: number;
}

export interface GeolocationGuardState {
  hasPermission: boolean;
  isBlocked: boolean;
  isChecking: boolean;
  coords: LocationCoords | null;
  errorMessage: string | null;
  requestLocation: () => Promise<LocationCoords | null>;
}

export function useGeolocationGuard(): GeolocationGuardState {
  const [hasPermission, setHasPermission] = useState<boolean>(false);
  const [isBlocked, setIsBlocked] = useState<boolean>(false);
  const [isChecking, setIsChecking] = useState<boolean>(false);
  const [coords, setCoords] = useState<LocationCoords | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const requestLocation = useCallback((): Promise<LocationCoords | null> => {
    setIsChecking(true);
    setErrorMessage(null);

    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        setHasPermission(false);
        setIsBlocked(true);
        setIsChecking(false);
        setErrorMessage('Seu dispositivo ou navegador não suporta geolocalização.');
        resolve(null);
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const loc = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          };
          setCoords(loc);
          setHasPermission(true);
          setIsBlocked(false);
          setIsChecking(false);
          setErrorMessage(null);
          resolve(loc);
        },
        (error) => {
          setHasPermission(false);
          setIsBlocked(true);
          setIsChecking(false);
          let msg = 'Autorização de acesso à localização é obrigatória para a continuidade da pesquisa.';
          if (error.code === error.PERMISSION_DENIED) {
            msg = 'Permissão de localização negada pelo usuário ou dispositivo.';
          } else if (error.code === error.POSITION_UNAVAILABLE) {
            msg = 'Não foi possível obter a localização atual do dispositivo.';
          } else if (error.code === error.TIMEOUT) {
            msg = 'Tempo limite excedido ao obter a localização do dispositivo.';
          }
          setErrorMessage(msg);
          resolve(null);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        }
      );
    });
  }, []);

  return {
    hasPermission,
    isBlocked,
    isChecking,
    coords,
    errorMessage,
    requestLocation,
  };
}
